import { Property } from '@propertypal/shared/src/types';
import platform from '@propertypal/shared/src/utils/platform';
import {
  calculatePercentagesFromArrayOfImages,
  getGalleryChunks,
} from '@propertypal/shared/src/utils/property/galleryHelpers';
import getGalleryImages from '@propertypal/shared/src/utils/property/getGalleryImages';
import { Hr } from '@propertypal/web-ui/src/layout';
import PropertyPrice from '@propertypal/web-ui/src/property/PropertyPrice';
import EnergyRatingIcon from '@propertypal/web-ui/src/property-box/EnergyRatingIcon';
import { SubHeading, Text } from '@propertypal/web-ui/src/typography';
import React, { useState } from 'react';
import config from '../../../../../app.config.json';
import {
  Container,
  SpaceBetween,
  InfoText,
  Logo,
  TitleAndLogo,
  KeyInfo,
  KeyInfoTable,
  AgentBox,
  AgentInfo,
  AgentNumber,
  ImageRow,
  BottomBar,
  Content,
  ImagesOption,
} from './PropertyPrintDetails.style';

interface Props {
  property: Property;
}

export const getDisplayLogo = (property: Property) => {
  if (property.agentLogos) {
    const logos = property.agentLogos.filter((l) => l.logoType === 'PREMIER');
    const developerLogo = logos.find((l) => l.developer);
    if (developerLogo) return developerLogo.url;
    if (logos.length === 1) return logos[0].url;
  }

  return '';
};

const PropertyPrintDetails: React.FC<Props> = (props) => {
  const [checked, setChecked] = useState<boolean>(true);
  const { property } = props;
  const images = getGalleryImages(props.property, '', false, false, true, true);
  const chunks = getGalleryChunks(images, [2, 3]);
  const hasRatesInfo = property.keyInfo?.some((info) => info.key === 'RATES');
  const agents = props.property.contacts?.all.filter((contact) => contact.agent);
  const displayLogo = getDisplayLogo(props.property);

  return (
    <Container data-testid="propertyDetails">
      <TitleAndLogo>
        <SubHeading>{property.displayAddress}</SubHeading>

        {displayLogo && <Logo src={displayLogo} />}
      </TitleAndLogo>

      <InfoText>{property.briefText}</InfoText>

      <PropertyPrice as="p" price={property.price} fontSize={16} />

      <ImagesOption>
        <input
          type="checkbox"
          name="printExtraInfo"
          id="printExtraInfo"
          checked={checked}
          onChange={() => setChecked(!checked)}
        />
        Print additional images &amp; map (disable to save ink)
      </ImagesOption>

      {!!property.images?.length && (
        <img alt={`${property.images[0].displayOrder}.jpg`} src={property.images[0].url} style={{ maxWidth: '100%' }} />
      )}

      <SpaceBetween>
        {property.contacts?.default?.phoneNumber && (
          <Text fontSize={14}>
            Telephone: <b>{property.contacts.default.phoneNumber.displayNumber}</b>
          </Text>
        )}

        {!property.contacts?.default && <div />}

        <Text fontSize={14}>
          View online:{' '}
          <b>
            {platform.domain}/{property.pathId}
          </b>
        </Text>
      </SpaceBetween>

      <Hr />

      <KeyInfo>
        <SubHeading style={{ margin: '21px 0' }}>Key Information</SubHeading>
        <KeyInfoTable>
          <tbody>
            {property.keyInfo?.map((item) => {
              if (item.key === 'STAMP_DUTY_COMPLEX') {
                return null;
              }

              if (item.key === 'BER') {
                return (
                  <tr key={item.key}>
                    <th>{item.name}</th>
                    <td>
                      <EnergyRatingIcon property={property} />
                    </td>
                  </tr>
                );
              }

              return (
                <tr key={item.key}>
                  <th>{item.name}</th>
                  <td>{item.text}</td>
                </tr>
              );
            })}
          </tbody>
        </KeyInfoTable>

        {hasRatesInfo && (
          <Text fontSize={11}>
            Rates information is provided for guidance purposes only, as the information source is subject to change.
            Please check the LPSNI website for further detail.
          </Text>
        )}
      </KeyInfo>

      {!!property.notes?.length && (
        <>
          <SubHeading style={{ margin: '20px 0' }}>Notes</SubHeading>

          {property.notes.map((note) => (
            <React.Fragment key={note.id}>
              <Text>
                <b>{note.selfAuthored ? 'You' : `${note.firstName} ${note.surname}`} wrote:</b>
              </Text>
              <Text>{note.content}</Text>
            </React.Fragment>
          ))}

          <Hr style={{ margin: '20px 0' }} />
        </>
      )}

      {property.description && <Content dangerouslySetInnerHTML={{ __html: property.description }} />}

      <Hr />

      {agents &&
        agents.map((contact) => (
          <AgentBox key={contact.organisation}>
            <SubHeading>{contact.organisation}</SubHeading>

            <AgentInfo>
              <img src={contact.logos?.standard} alt="agent logo" width={200} style={{ marginRight: 20 }} />

              {contact.phoneNumber && (
                <AgentNumber>
                  <Text fontSize={20} style={{ margin: 10 }}>
                    {contact.phoneNumber.displayNumber}
                  </Text>
                </AgentNumber>
              )}
            </AgentInfo>

            {!!contact.psrLicenceNumber && <Text mt={10}>PSR Licence: {contact.psrLicenceNumber}</Text>}
          </AgentBox>
        ))}

      {checked && (
        <>
          <Hr data-testid="photo-section" />
          {property.images && (
            <>
              <SubHeading>Photo Gallery</SubHeading>

              {chunks.map((chunk) => {
                const imageWidths = calculatePercentagesFromArrayOfImages(chunk.images, 580);

                return (
                  <ImageRow key={chunk.keyItem}>
                    {chunk.images.map((image, index) => (
                      <img
                        key={image.image}
                        src={image.image}
                        alt={`${image.displayOrder}.jpg`}
                        width={image.width}
                        height={image.height}
                        style={{ ...imageWidths[index] }}
                      />
                    ))}
                  </ImageRow>
                );
              })}

              <Hr />
            </>
          )}

          <SubHeading>Location</SubHeading>

          {property.coordinate && (
            <img
              alt="Property location"
              // eslint-disable-next-line max-len
              src={`https://maps.googleapis.com/maps/api/staticmap?sensor=false&key=${config.googleMapsApiKey}&zoom=${property.coordinate.mapZoomLevel}&size=580x400&&markers=size:mid|${property.coordinate.latitude},${property.coordinate.longitude}`}
            />
          )}
        </>
      )}
      <BottomBar>
        <p style={{ padding: '13px 0' }}>Visit {platform.domain} for further details.</p>
      </BottomBar>
    </Container>
  );
};

export default PropertyPrintDetails;
